import { Drop, Wind } from "@phosphor-icons/react";
import { AlertTriangle, Camera, Check, Loader2, Minus, X } from "lucide-react";
import { useEffect, useState } from "react";

import {
  type AirthingsSensorStatus,
  getApiErrorMessage,
  type Room,
  useAirthingsStatus,
} from "@/lib/api";
import { downscaleToJpeg, pickImage } from "@/lib/image-capture";
import { logger } from "@/lib/logger";
import { absorbedBy, activeDevices, liveRooms, retiredSensors } from "@/lib/rooms";

import { AppBar, HelpHint, MaterialsNeeded, PhaseDots, PillButton, StuckHelp } from "./components";
import { PRODUCT_NAMES } from "./product-names";

const AIR_QUALITY_CONNECTOR = PRODUCT_NAMES.airQualityConnector;

/**
 * Compartment 2 · Air Quality. Runs in TWO parts, minutes apart.
 *
 * Part A (early): purpose → wire up the connector → wake the sensors → hand-off.
 * Part B (closing task, after every compartment): where they go → how to place
 * them → the placement checklist → the per-sensor reading check.
 *
 * Why split: the sensors pair with the connector by sitting next to it, and that
 * takes a while. Doing it in one sitting meant standing in front of a disabled
 * Continue button waiting for four sensors to report. So Part A wakes them and
 * walks away, the pairing happens during compartments 3 to 5, and Part B places
 * sensors that are already talking.
 *
 * The split is frontend-only. `air_sensors` is a single persisted step key owned
 * by the backend, so Part A completes it and Part B hangs off the end of the
 * flow (see `closingTask` in flow-shell). One consequence worth knowing: the
 * backend reads compartment 2 as finished while the sensors are still beside the
 * connector.
 *
 * Real wiring: the hub has no heartbeat of its own — sensors relay through it,
 * so it can only be proven online once a sensor reports, which cannot happen
 * before the sensors are placed and activated. The hub step therefore confirms
 * the wiring and checks only that the kit is REGISTERED to this building; all
 * liveness verification happens in the end-of-flow reading check, which polls
 * the cloud per sensor. Rooms come from admin-entered config; a small fallback
 * keeps the flow walkable if a household has no rooms yet.
 */

const FALLBACK_ROOMS: Room[] = [
  { name: "Kitchen", area_m2: 14 },
  { name: "Living room", area_m2: 28 },
  { name: "Master bedroom", area_m2: 16 },
  { name: "Home office", area_m2: 11 },
];

/** Poll cadence for the AirThings cloud during the reading check. */
const READING_POLL_MS = 3000;

/**
 * How long the reading check waits on silence before pointing at the hub.
 * A freshly placed sensor genuinely takes minutes to first report, so blaming
 * the hub early would send people chasing the wrong thing; only sustained
 * silence from EVERY sensor makes the hub the likely cause.
 */
const HUB_SUSPECT_AFTER_MS = 8 * 60 * 1000;

/**
 * How long the reading check holds someone before offering a way out.
 *
 * Short on purpose. By the time Part B runs, the sensors have been paired for
 * most of an hour, so a reading should arrive within seconds. If it has not
 * after this long, something is wrong and the user must not be trapped on the
 * final screen of a sixty-minute job: a person who cannot finish force-quits,
 * which lands in the pilot data as abandonment and corrupts the exact number
 * this trial exists to measure.
 */
const FINISH_ANYWAY_AFTER_MS = 90 * 1000;

/** Air Quality's flat module block — brand Background Blue. See the tint map in
 *  globals.css; the same colour marks this module on the guide. */
const MODULE_HERO = "bg-sky-subtle";

/** "14 m²", or "" when no area is configured for the room. */
function formatArea(room: Room): string {
  return room.area_m2 == null ? "" : `${room.area_m2} m²`;
}

/** The stored AirThings sensor id for a room, if one is configured. */
function sensorId(room: Room): string | null {
  // A retired sensor (decommissioned by a merge) must never come up for
  // placement or readings, whichever record it sits on; `retiredSensors` in
  // lib/rooms is where the spare is looked up for its own card.
  return activeDevices(room).find((d) => d.device_type === "AirthingsSensor")?.external_id ?? null;
}

interface RoomReadingCardProps {
  /** Room name, as configured for the household. */
  room: string;
  /** That room's live cloud row, when the sensor is matched and reporting. */
  status?: AirthingsSensorStatus;
  /** Sensor the household left for later — no numbers to show, and none coming. */
  deferred?: boolean;
}

/**
 * One room's tile on the finished screen — the house talking back.
 *
 * By this point the flow has told the household four times that a sensor is
 * "reporting", which is a claim about our plumbing. Their own kitchen at 21.5°
 * is the first thing on screen that they can check against the room they are
 * standing in, and that is what makes an hour of cabling feel finished.
 *
 * Every number is optional: the cloud answers `responding: true` off a
 * heartbeat, and a sensor can be alive with its first sample still on the way.
 * So each line renders only if it has a value, and a tile with nothing to show
 * falls back to saying so rather than printing a dash.
 */
function RoomReadingCard({ room, status, deferred }: RoomReadingCardProps) {
  const temp = status?.temp;
  const humidity = status?.humidity;
  const co2 = status?.co2;
  const hasReadings = temp != null || humidity != null || co2 != null;
  return (
    // `min-w-0` so a long room name truncates instead of widening its grid
    // track and pushing the pair out of alignment.
    <div
      className={`bg-daylight-subtle flex min-w-0 flex-col gap-1 rounded-2xl p-4 ${deferred ? "opacity-60" : ""}`}
    >
      <p className="text-primary type-caption truncate">{room}</p>
      {deferred ? (
        <p className="text-muted type-body-sm">Left for later</p>
      ) : hasReadings ? (
        <>
          {temp != null && <p className="text-primary text-[28px] leading-9">{temp.toFixed(1)}°</p>}
          {humidity != null && (
            <p className="text-muted type-body-sm flex items-center gap-1.5">
              <Drop weight="light" className="size-4 shrink-0" aria-hidden />
              {Math.round(humidity)}% humidity
            </p>
          )}
          {co2 != null && (
            <p className="text-muted type-body-sm flex items-center gap-1.5">
              <Wind weight="light" className="size-4 shrink-0" aria-hidden />
              {Math.round(co2)} ppm CO₂
            </p>
          )}
        </>
      ) : (
        <p className="text-success type-body-sm flex items-center gap-1.5">
          <Check className="size-3.5 shrink-0" strokeWidth={3} aria-hidden />
          Reporting
        </p>
      )}
    </div>
  );
}

/** The AI verdict on a placement photo (mirrors the backend PlacementCheck). */
interface PlacementResult {
  ok: boolean;
  verified: boolean;
  message: string;
}

/**
 * The stages of each half, in order.
 *
 * These two lists are the source of truth: `STAGES` is composed from them and
 * `Stage` is derived from that, so a new stage is added in exactly one place and
 * the gallery, the type and the dot map cannot drift apart.
 */
export const PART_A_STAGES = [
  // Part A, done early: wire the connector up and wake the sensors so they pair
  // in the background while the rest of the kit gets installed.
  "purpose",
  "hub",
  "wake",
  "handoff",
] as const;

export const PART_B_STAGES = [
  // Part B, the closing task once every compartment is done: place the paired
  // sensors around the house and confirm each one is reporting.
  "place",
  "tips",
  "checklist",
  "detecting",
  "confirmed",
  "done",
] as const;

/** Every stage of this module, in order — the dev screen gallery walks it. */
export const STAGES = [...PART_A_STAGES, ...PART_B_STAGES] as const;

export type Stage = (typeof STAGES)[number];

/**
 * Which dot is lit. Each part gets its own run of dots: they are separate
 * visits, most of an hour apart, with three compartments in between. Sharing
 * one indicator across both would tell a user in Part B that they are halfway
 * through something they finished ages ago.
 *
 * The photo side-branch sits on the checklist's dot, because it is a detour
 * from that screen rather than a step of its own.
 */
/**
 * Dots shown in Part B. Four, not six: the photo side-branch shares the
 * checklist's dot, so it does not read as two extra steps.
 */
const PART_B_DOTS = 4;

const DOT_INDEX: Record<Stage, number> = {
  purpose: 0,
  hub: 1,
  wake: 2,
  handoff: 3,
  place: 0,
  tips: 1,
  checklist: 2,
  detecting: 2,
  confirmed: 2,
  done: 3,
};

interface AirSensorsScreenProps {
  rooms: Room[];
  busy: boolean;
  error: string;
  onComplete: () => void;
  /**
   * Which half of the compartment to run. "a" wires the connector up and wakes
   * the sensors; "b" is the closing task that places them. Sensors need to sit
   * beside the connector for a while to pair, so the wait happens during the
   * other compartments instead of in front of a spinner.
   */
  part?: "a" | "b";
  /**
   * Display names of the modules this household finished, in the order the
   * guide listed them. Only the closing screen uses them, to name what the last
   * hour bought; passed in rather than derived here because the flow state
   * lives in `flow-shell` and this screen only ever sees its own rooms.
   */
  completedSteps?: string[];
  /** Modules skipped along the way, listed under the recap as not set up. */
  skippedSteps?: string[];
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
  /**
   * Stand-in cloud rows for the dev screen gallery's `done-victory` stage.
   *
   * The victory view only appears once real sensors report, which the gallery
   * has no way to bring about — so it hands over fixture readings instead.
   * Their presence also seeds every sensor as placed, because a household that
   * placed nothing has nothing to verify and would sit on the checking view.
   *
   * When set it replaces the cloud entirely: the query is switched off and
   * these rows answer for it. Deliberate — a reviewer's dev session is usually
   * logged into a real building (the office kit), whose serials match none of
   * the gallery's rooms, so letting live data win left every tile blank on the
   * one screen this exists to show. Production omits the prop, so this branch
   * cannot fake a reading on a real install.
   */
  previewReadings?: AirthingsSensorStatus[];
}

export function AirSensorsScreen({
  rooms,
  busy,
  error,
  onComplete,
  initialStage,
  completedSteps = [],
  skippedSteps = [],
  previewReadings,
  part = "a",
}: AirSensorsScreenProps) {
  // Rooms with a pre-provisioned sensor serial are the inventory to place
  // and verify; rooms without one only appear when nothing is configured
  // yet (all rooms → demo fallback), keeping the flow walkable.
  // Absorbed rooms never reach the walk: their sensor is retired and their
  // space is covered by the room that absorbed them.
  const roomsWithSensors = liveRooms(rooms).filter((room) => sensorId(room) !== null);
  // Sensors a merge retired. They are real hardware the resident is holding,
  // so the checklist says what to do with them rather than pretending they
  // do not exist: put them anywhere, read them, know SmartClimate ignores them.
  const spareSensors = retiredSensors(rooms);
  // The fallbacks stay on live rooms too, or an absorbed room would sneak back
  // into the walk the moment no live room carries a sensor id.
  const live = liveRooms(rooms);
  const sensors =
    roomsWithSensors.length > 0 ? roomsWithSensors : live.length > 0 ? live : FALLBACK_ROOMS;
  const [stage, setStage] = useState<Stage>(initialStage ?? (part === "b" ? "place" : "purpose"));
  // Which sensors the user has marked placed; index-aligned with `sensors`.
  // (The gallery's victory fixture opens with the walk of the house already
  // done — see `previewReadings`.)
  const [placed, setPlaced] = useState<boolean[]>(() => sensors.map(() => previewReadings != null));
  /**
   * Sensors deliberately left for later: a locked room, a room mid-decoration,
   * a spot the household wants to think about. Without this the checklist is a
   * dead end, because one unplaceable sensor blocks the whole install at the
   * very last screen. Deferred sensors are excluded from the reading check, so
   * they cannot hold up the finish either.
   */
  const [deferred, setDeferred] = useState<boolean[]>(() => sensors.map(() => false));
  // The sensor being photo-confirmed in the detecting/confirmed stages.
  const [photoIndex, setPhotoIndex] = useState(0);
  // Object URL of the just-captured photo, shown in the confirm frame.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  // AI verdict for the current photo (null while it's being analysed).
  const [photoResult, setPhotoResult] = useState<PlacementResult | null>(null);

  // Real hub check AND end-of-flow reading check both read the AirThings
  // cloud: the hub-check stage waits for the building to come online, the
  // done stage confirms each placed sensor is still reporting fresh data.
  const airthings = useAirthingsStatus({
    // The gallery's fixture rows stand in for the cloud, so don't call it.
    enabled: previewReadings == null && (stage === "hub" || stage === "done"),
    // Only the reading check polls; the hub screen just needs one read to
    // confirm the kit is registered to this building.
    refetchInterval: stage === "done" ? READING_POLL_MS : undefined,
  });
  const allSensorsLive = airthings.data?.connected === true;
  // Registration facts, available without any sensor having reported yet:
  // the hub and sensors appear in the building listing once provisioned.
  const registrationProblem =
    airthings.data == null
      ? null
      : airthings.data.hub_serial == null
        ? `We can't see your ${AIR_QUALITY_CONNECTOR} registered to this home yet.`
        : (airthings.data.sensors?.length ?? 0) === 0
          ? "We can't see any sensors registered to this home yet."
          : null;

  // Sustained silence from every sensor is what makes the hub suspect — see
  // HUB_SUSPECT_AFTER_MS.
  const [hubSuspect, setHubSuspect] = useState(false);
  const [mayFinishAnyway, setMayFinishAnyway] = useState(false);
  useEffect(() => {
    if (stage !== "done") {
      return;
    }
    setHubSuspect(false);
    // Nothing placed means nothing to verify: the reading check can never
    // succeed, so the escape hatch opens immediately instead of after 90s.
    setMayFinishAnyway(!placed.some(Boolean));
    const suspect = setTimeout(() => setHubSuspect(true), HUB_SUSPECT_AFTER_MS);
    const escape = setTimeout(() => setMayFinishAnyway(true), FINISH_ANYWAY_AFTER_MS);
    return () => {
      clearTimeout(suspect);
      clearTimeout(escape);
    };
  }, [stage, placed]);

  // Per-serial reporting state from the cloud, for the reading check — or the
  // gallery's fixture rows, which replace it outright (see `previewReadings`).
  const sensorsBySerial = new Map(
    (previewReadings ?? airthings.data?.sensors ?? []).map((s) => [s.serial_number, s]),
  );

  /**
   * The cloud's row for a room's sensor, when the room has a serial configured
   * and this building reports it. Undefined covers both gaps, which is also why
   * the finished screen treats a missing row as "no numbers yet" rather than as
   * a sensor that is down.
   */
  function roomSensor(room: Room): AirthingsSensorStatus | undefined {
    const id = sensorId(room);
    return id ? sensorsBySerial.get(id) : undefined;
  }

  /** Whether a room's sensor is reporting, with its last-seen age (minutes). */
  function roomReading(room: Room): { reading: boolean; lastSeenMinutes: number | null } {
    const match = roomSensor(room);
    if (match) {
      return { reading: match.responding, lastSeenMinutes: match.last_seen_minutes ?? null };
    }
    // No configured id, or an id this building doesn't report — fall back to
    // the building-level signal so a config gap can't dead-end the flow
    // (reaching this stage already required the hub to be connected).
    return { reading: allSensorsLive, lastSeenMinutes: null };
  }

  // Only the sensors that were actually placed have to report. A deferred one
  // is sitting in a drawer by definition, so waiting on it would never end.
  const toVerify = sensors.filter((_, i) => placed[i]);
  // `.every()` on an empty list is true, so the length check is load-bearing:
  // a household that deferred every sensor has verified nothing, and must not
  // be told its home is sensing over a grid of "Left for later" tiles.
  const allReading = toVerify.every((s) => roomReading(s).reading);
  /** Every row resolved one way or the other, so the checklist can move on. */
  const allResolved = sensors.every((_, i) => placed[i] || deferred[i]);
  /** Some sensors are in a drawer, so "every sensor" would be a lie. */
  const someDeferred = deferred.some(Boolean);

  /**
   * The victory is latched, not live.
   *
   * The 3s poll keeps running on the done stage because the finished screen
   * shows live room readings. But a sensor that misses a couple of relays
   * drops out of `allReading`, and without this the celebration would snap
   * back to "Checking your sensors" with a disabled Finish button — retracting
   * a win the household already saw, at the last screen of an hour's work.
   * Once every placed sensor has reported at least once, it has been proven;
   * the readings underneath can flicker without unwinding that.
   */
  const [won, setWon] = useState(false);
  useEffect(() => {
    if (stage === "done" && toVerify.length > 0 && allReading) {
      setWon(true);
    }
  }, [stage, allReading, toVerify.length]);

  // Widened deliberately: PART_A_STAGES is a const tuple so its own `includes`
  // only accepts Part A's members, which is the opposite of the question here.
  const inPartA = (PART_A_STAGES as readonly Stage[]).includes(stage);
  const dotsTotal = inPartA ? PART_A_STAGES.length : PART_B_DOTS;
  const dotsIndex = DOT_INDEX[stage];

  const sensor = sensors[photoIndex] ?? sensors[0]!;
  const placedCount = placed.filter(Boolean).length;

  // Rooms and sensor locations are pre-defined by BOI/VELUX, so placement is a
  // confirmation the user can tick off (or double-check with a photo), not a
  // gate.
  const markPlaced = (i: number) => {
    setPlaced((prev) => prev.map((p, idx) => (idx === i ? true : p)));
    setDeferred((prev) => prev.map((d, idx) => (idx === i ? false : d)));
  };
  const markDeferred = (i: number) =>
    setDeferred((prev) => prev.map((d, idx) => (idx === i ? true : d)));

  // Capture a photo of sensor `i` and run it past the AI placement check.
  const capturePhoto = async (i: number) => {
    const file = await pickImage();
    if (!file) {
      return; // the camera/picker was cancelled
    }
    const image = await downscaleToJpeg(file);
    setPhotoIndex(i);
    setPhotoUrl(URL.createObjectURL(image));
    setPhotoResult(null);
    setStage("detecting");
    try {
      const form = new FormData();
      form.append("photo", image);
      form.append("room", sensors[i]?.name ?? "");
      const res = await fetch("/api/onboarding/air-sensors/verify-placement", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        throw new Error(`verify-placement ${res.status}`);
      }
      setPhotoResult((await res.json()) as PlacementResult);
    } catch (err) {
      logger.warn("[onboarding] placement check failed:", err);
      setPhotoResult({
        ok: true,
        verified: false,
        message: "We couldn't run the visual check, but you're good to continue.",
      });
    } finally {
      setStage("confirmed");
    }
  };

  // Once every sensor is placed, move into the reading check (which polls the
  // AirThings cloud via the `airthings` query above).
  const startReadingCheck = () => setStage("done");

  // ── Stage 0: intro — "Setting up Air Quality" (Figma "AirQuality start"
  //    1056:1268): why the sensors matter + the kit parts to gather.
  if (stage === "purpose") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="air_sensors" stage={stage} title={PRODUCT_NAMES.airQuality} />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          {/* The hero is the Connector, not a sensor: the first physical action
              of this compartment is plugging the Connector in, and the beta
              installs showed the opening image primes what people reach for
              first (Esben unpacked a sensor). The render sits on the module
              block via `mix-blend-multiply`: the ground takes the tint and the
              product keeps its own shading. */}
          <div className={`isolate aspect-[334/219] w-full shrink-0 rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/airquality-connector.png"
              alt={`The ${AIR_QUALITY_CONNECTOR}`}
              className="size-full rounded-2xl object-contain p-4 mix-blend-multiply"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Setting up Air Quality</h1>
            <p className="text-muted text-[17px] leading-6">
              <span className="font-bold">Why this part?</span> Your air sensors measure
              temperature, humidity, CO₂ and VOCs in every room. SmartClimate uses those readings to
              decide when to open a window or turn up the heat.
            </p>
          </div>
          <MaterialsNeeded
            items={[
              {
                title: AIR_QUALITY_CONNECTOR,
                caption: "Comes with a white ethernet cable and power cord.",
                img: "/brand/onboarding/airquality-connector.png",
                alt: `The ${AIR_QUALITY_CONNECTOR}`,
              },
              {
                title: "Air sensors",
                caption: "These are labeled per room.",
                img: "/brand/onboarding/airquality-sensors.png",
                alt: "An air sensor",
              },
            ]}
          />
          <p className="text-primary text-[17px] leading-6">
            Make sure you have everything ready before we start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("hub")} />
        </div>
      </div>
    );
  }

  // ── Stage 1: connect the AirThings hub (guide B1) ───────────────────────
  if (stage === "hub") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="air_sensors"
          stage={stage}
          title={PRODUCT_NAMES.airQuality}
          onBack={() => setStage("purpose")}
        />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            {/* Not "Connect your ... Connector": the echo reads badly. "Wire up"
                is also the more literal description of the job. */}
            <h1 className="text-primary type-headline">Wire up your {AIR_QUALITY_CONNECTOR}</h1>
            {/* Leads on familiarity: this is the same wiring they did in
                compartment 1, and saying so is worth more than re-explaining
                what a connector is. */}
            <p className="text-muted text-[17px] leading-6">
              This wires up exactly like your {PRODUCT_NAMES.hub}. You&apos;ll find it in
              compartment 2 with a white ethernet cable.
            </p>
          </div>
          <img
            src="/brand/onboarding/airquality-connector-hero.png"
            alt={`The ${AIR_QUALITY_CONNECTOR} cabled to a free LAN port on the router`}
            className="w-full rounded-2xl object-cover"
            style={{ aspectRatio: "1074/534" }}
          />
          <div className="flex flex-col gap-3">
            {[
              "Plug the white ethernet cable into the connector, then into any free LAN port on your router or network switch.",
              "Plug the connector's power cable into your power strip.",
              "Watch the lights on the front. Power goes green right away. The cloud light can take up to 3 minutes.",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
                  <span className="text-primary type-caption">{i + 1}</span>
                </div>
                <p className="text-muted text-[15px] leading-[22px]">{step}</p>
              </div>
            ))}
          </div>
          {/* The end state, right under the step that describes it: three green
              lights is the thing the CTA asks them to confirm, so showing it
              beats describing it. */}
          <img
            src="/brand/onboarding/airquality-connector-confirmation-light.png"
            alt="The connector's power, cloud and network lights all lit green"
            className="mx-auto w-full max-w-[280px]"
          />
          <HelpHint
            question="Cloud light still off after 3 minutes?"
            answer="Check both cable ends are clicked in firmly. Still off? Unplug the connector's power, wait 10 seconds, plug it back in."
          />
          {/* Registration, not liveness: the hub only proves it's online once a
              sensor relays through it, which can't happen before the sensors
              are placed and activated. What we CAN check now is that the kit is
              registered to this household's building — a mismatch there is a
              support call, and far cheaper to catch before walking the house. */}
          {registrationProblem && (
            <div className="bg-warning/10 flex flex-col gap-2 rounded-2xl p-4">
              <p className="text-primary text-[15px] leading-[22px] font-medium">
                {registrationProblem}
              </p>
              <p className="text-muted type-body-sm">
                You can carry on placing the sensors. We&apos;ll check them again at the end.
              </p>
              <StuckHelp
                stepKey="air_sensors"
                stage="hub_registration"
                lastError={registrationProblem}
                seed={`The app says my ${AIR_QUALITY_CONNECTOR} isn't registered to my home. Can you help?`}
                label="Ask about this"
              />
            </div>
          )}
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="The cloud light is solid green" onClick={() => setStage("wake")} />
        </div>
      </div>
    );
  }

  // ── Part A · wake the sensors so they pair while the user works ─────────
  //
  // The CTA is an observation, not a self-report: waving at a sensor makes it
  // answer, and this is the only cheap moment to catch one that never woke up.
  // Caught here it costs seconds; caught in Part B it costs a second lap of the
  // house.
  if (stage === "wake") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="air_sensors"
          stage={stage}
          title={PRODUCT_NAMES.airQuality}
          onBack={() => setStage("hub")}
        />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Wake your sensors</h1>
            <p className="text-muted text-[17px] leading-6">
              They pair with the connector on their own, and that takes a while. So we start it now
              and place them at the end.
            </p>
          </div>
          <img
            src="/brand/onboarding/airquality-sensor-tab.png"
            alt="The back of an air sensor with the green battery tab being pulled out"
            className="w-full rounded-2xl"
          />
          <div className="flex flex-col gap-3">
            {[
              "Pull the green tab out of the back of each sensor.",
              `Lay them next to the ${AIR_QUALITY_CONNECTOR}, screens up.`,
              "Wave your hand in front of one. A red light flashes and the screen shows SMARTLINK. Red is right for now.",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
                  <span className="text-primary type-caption">{i + 1}</span>
                </div>
                <p className="text-muted text-[15px] leading-[22px]">{step}</p>
              </div>
            ))}
          </div>
          {/* Two images, one per action that needs showing: pulling the tab, and
              the wave test. The sensors-beside-connector shot lives on the
              hand-off screen instead, where "leave them next to the connector"
              is the entire message rather than a detail of step 2. */}
          <img
            src="/brand/onboarding/airquality-sensor-wave-red.png"
            alt="A hand waving in front of a sensor, its red light lit and SMARTLINK on the screen"
            className="w-full rounded-2xl"
          />
          <HelpHint
            question="Why is the light red?"
            answer="Red means the sensor is awake but hasn't paired yet. It turns white once it has, and each sensor takes its own time."
          />
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="A light flashed on each one" onClick={() => setStage("handoff")} />
        </div>
      </div>
    );
  }

  // ── Part A · the hand-off. Answers "why am I not placing these now?" ─────
  if (stage === "handoff") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="air_sensors"
          stage={stage}
          title={PRODUCT_NAMES.airQuality}
          onBack={() => setStage("wake")}
        />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-6">
          <img
            src="/brand/onboarding/airquality-sensors-beside-connector.png"
            alt={`Air sensors pairing beside the ${AIR_QUALITY_CONNECTOR}`}
            className="w-full rounded-2xl"
          />
          <div className="flex flex-col gap-3">
            {/* The proximity requirement IS the headline: buried in body text it
                was skimmed past in the beta (Michael), and it's the one thing
                that breaks pairing if ignored. */}
            <h1 className="text-primary type-headline">Leave them next to the connector</h1>
            <p className="text-muted text-[17px] leading-6">
              They pair on their own while you work. Each light turns white once its sensor is
              paired, usually within 15 minutes. They won&apos;t all turn white at once, so a slow
              one is not a fault. We&apos;ll come back at the end to put them in their rooms.
            </p>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          {error && (
            <p role="alert" className="text-destructive pb-3 text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton label="See what's next" busy={busy} onClick={onComplete} />
        </div>
      </div>
    );
  }

  // ── Part B · where the sensors are going ─────────────────────────────────
  if (stage === "place") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="air_sensors" stage={stage} title={PRODUCT_NAMES.airQuality} />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-6">
          <div className={`flex h-[203px] items-center justify-center rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/airthings-sensor.png"
              alt="An air sensor"
              className="h-[143px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-title">Time to place your sensors</h1>
            <p className="text-muted text-[17px] leading-6">
              Your {sensors.length} air sensors have been pairing while you worked. Now they go in
              their rooms, where they&apos;ll set a baseline tonight.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-muted type-label">We&apos;ll place them here</p>
            <div className="flex flex-wrap gap-2">
              {sensors.map((s) => (
                <span
                  key={s.name}
                  className="bg-background text-primary rounded-2xl px-3 py-1.5 text-[15px] leading-[22px]"
                >
                  {s.name}
                </span>
              ))}
            </div>
            <p className="text-muted type-body-sm">The sensors are already labeled</p>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="Show me how" onClick={() => setStage("tips")} />
        </div>
      </div>
    );
  }

  // ── Part B · how to place them well ─────────────────────────────────────
  if (stage === "tips") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="air_sensors"
          stage={stage}
          title={PRODUCT_NAMES.airQuality}
          onBack={() => setStage("place")}
        />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">A good spot matters</h1>
            <p className="text-muted text-[17px] leading-6">
              Where a sensor sits changes what it reads.
            </p>
          </div>
          {/* This diagram replaced six lines of prose, and it earns the space:
              height and clearance are spatial facts, so a picture states them
              better than a sentence and survives translation.

              It also marks what to avoid (vent, radiator, humidifier, metal
              bin), which is why the three-item "Avoid" list below it is now one
              line instead of three bullets. That line stays rather than being
              deleted: it is the only version of this information available to a
              screen reader, and it is faster to re-scan than a photo when
              someone is standing in a room deciding. */}
          <img
            src="/brand/onboarding/airquality-placement-diagram.png"
            alt="A sensor on a shelf 1.1 to 1.8 m off the floor, at least 90 cm from the window, with crosses marking the vent, radiator, humidifier and metal bin to keep away from"
            className="w-full rounded-2xl"
          />
          <div className="flex items-start gap-3">
            <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-full">
              <X className="text-destructive size-4" strokeWidth={3} />
            </div>
            <p className="text-muted text-[15px] leading-[22px]">
              Keep clear of direct sunlight, radiators, humid spots and large metal objects.
            </p>
          </div>
          {/* Was a permanent panel. It is a consequence warning rather than an
              instruction, and "can I move it later?" is the exact question a
              person asks, so a hint answers it in place and gives the diagram
              the room it needs. */}
          <HelpHint
            question="Can I move it later?"
            answer="Try not to. A sensor needs about 30 days in one spot to learn the room, and moving it means starting over with a reset."
          />
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I'm ready to place them" onClick={() => setStage("checklist")} />
        </div>
      </div>
    );
  }

  // ── The finish line: every placed sensor is reporting, so the last screen of
  //    a ~50-minute install stops verifying and starts celebrating. ──────────
  //
  // Same stage as the reading check below, split on its outcome rather than
  // given a stage of its own: the household never navigates here, the poll
  // simply comes good underneath them. Everything on this screen is a payoff —
  // the confidence check the flow uses at every save point, blown up; their own
  // rooms' numbers, which is the first proof on screen that isn't our word for
  // it; and the compartments they closed. One CTA, because there is one thing
  // left to do.
  if (stage === "done" && won) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        {/* A distinct stage token, not `stage`. Both `done` branches render an
            AppBar at the same spot in the tree, so React keeps one instance
            across the swap and its scroll reset only fires if a dep actually
            changes — with `stage` on both, the victory view would inherit the
            checking view's scroll offset. It also gives the help assistant a
            truer picture than "they're still waiting". */}
        <AppBar stepKey="air_sensors" stage="done_victory" title={PRODUCT_NAMES.airQuality} />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-7 px-7 pt-6">
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="bg-success flex size-24 items-center justify-center rounded-full">
              <Check className="size-12 text-white" strokeWidth={3} aria-hidden />
            </div>
            {/* Announced: this screen arrives on its own while the household is
                watching a spinner, so a screen-reader user gets no other signal
                that the wait ended. */}
            <div role="status" className="flex flex-col gap-3">
              <h1 className="text-primary type-title">Your home is sensing</h1>
              <p className="text-muted text-[17px] leading-6">
                {someDeferred ? "Your placed sensors are online." : "Every sensor is online."} Over
                the next 24 hours they&apos;ll learn what comfortable feels like — room by room.
              </p>
              {/* The one line that answers "am I done?". It sits under the
                  explanation rather than above it because the CTA is the next
                  thing the eye lands on. */}
              <p className="text-primary type-body-strong">
                That&apos;s everything set up. One tap left.
              </p>
            </div>
          </div>
          {/* Every sensor gets a tile, whether it is reporting or was left for
              later: reaching this stage required each one to be resolved one way
              or the other, so the grid is the household's full inventory and a
              missing room would read as a sensor we lost. */}
          <div className="flex flex-col gap-3">
            <p className="text-muted type-label">Live from your rooms</p>
            <div className="grid grid-cols-2 gap-2">
              {sensors.map((room, i) => (
                <RoomReadingCard
                  key={room.name}
                  room={room.name}
                  status={roomSensor(room)}
                  deferred={deferred[i]}
                />
              ))}
            </div>
          </div>
          {(completedSteps.length > 0 || skippedSteps.length > 0) && (
            <div className="flex flex-col gap-3">
              {/* A household that skipped everything still gets the recap:
                  hiding it when nothing completed is how skips vanished from
                  this surface in the first place. The label follows the
                  content, since "what you set up" over zero successes would
                  be its own small lie. */}
              <p className="text-muted type-label">
                {completedSteps.length > 0 ? "What you set up" : "Skipped during setup"}
              </p>
              <div className="flex flex-col gap-2">
                {completedSteps.map((name) => (
                  <div key={name} className="flex items-center gap-3">
                    <div className="bg-success flex size-6 shrink-0 items-center justify-center rounded-full">
                      <Check className="size-3.5 text-white" strokeWidth={3} aria-hidden />
                    </div>
                    <p className="text-primary text-[15px] leading-[22px]">{name}</p>
                  </div>
                ))}
                {/* Skipped modules stay on the recap rather than vanishing
                    from it. Omission read as completeness: nothing on the
                    closing screens said the skipped hardware was not running
                    (VLX-76). A minus in yellow, never a check. */}
                {skippedSteps.map((name) => (
                  <div key={name} className="flex items-center gap-3">
                    <div className="bg-daylight flex size-6 shrink-0 items-center justify-center rounded-full">
                      <Minus className="text-primary size-3.5" strokeWidth={3} aria-hidden />
                    </div>
                    <p className="text-primary flex-1 text-[15px] leading-[22px]">{name}</p>
                    <span className="text-warning type-caption">Skipped</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton label="Finish setup" busy={busy} onClick={onComplete} />
        </div>
      </div>
    );
  }

  // ── Reading check: with every sensor resolved, poll the AirThings cloud
  //    until each placed one is reporting fresh data. Comes good into the
  //    screen above.
  //
  //    Also where a household that deferred every sensor lands and stays:
  //    there is nothing to verify, so there is nothing to celebrate, and the
  //    finish-anyway escape hatch below is their way out. ──────────────────────
  if (stage === "done") {
    const checkError = airthings.error
      ? getApiErrorMessage(airthings.error, "We can't reach your sensors yet.")
      : null;
    const readingCount = toVerify.filter((s) => roomReading(s).reading).length;
    // Only sustained silence from EVERY sensor implicates the hub: if any one
    // reports, the hub is demonstrably relaying and the problem is that sensor.
    const blameHub = readingCount === 0 && hubSuspect;
    const waitingCopy = blameHub
      ? `No sensor has reported yet. Check the ${AIR_QUALITY_CONNECTOR}'s light is solid green and its cable is still in. Every sensor relays through it.`
      : readingCount === 0
        ? "They've been paired for a while, so this is usually quick. Keep this screen open."
        : `${readingCount} of ${toVerify.length} reporting. Waiting on the rest.`;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="air_sensors" stage={stage} title={PRODUCT_NAMES.airQuality} />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-primary type-headline">Checking your sensors</h1>
            <p className="text-muted text-[17px] leading-6">{checkError ?? waitingCopy}</p>
            <StuckHelp
              stepKey="air_sensors"
              stage="reading_check"
              lastError={checkError ?? airthings.data?.detail}
              seed="Some of my air sensors still aren't reporting. Can you check which ones and why?"
              label="Waiting too long? Ask for help"
              afterMs={HUB_SUSPECT_AFTER_MS}
            />
          </div>
          <div className="flex flex-col gap-1">
            {sensors.map((s, i) => {
              const { reading, lastSeenMinutes } = roomReading(s);
              const id = sensorId(s);
              const isDeferred = deferred[i];
              const subtitle = isDeferred
                ? "Left for later"
                : reading
                  ? lastSeenMinutes === null
                    ? "Reporting"
                    : lastSeenMinutes <= 0
                      ? "Reading just now"
                      : `Last reading ${lastSeenMinutes} min ago`
                  : id
                    ? `Serial ${id}`
                    : `Sensor ${i + 1} of ${sensors.length}`;
              return (
                <div
                  key={s.name}
                  className="bg-background flex items-center gap-3 rounded-2xl px-4 py-2.5"
                >
                  <div
                    className={`flex size-9 shrink-0 items-center justify-center rounded-full ${
                      reading && !isDeferred ? "bg-success" : "bg-surface-subtle"
                    }`}
                  >
                    {isDeferred ? (
                      <X className="text-muted size-4.5" />
                    ) : reading ? (
                      <Check className="size-4.5 text-white" strokeWidth={3} />
                    ) : (
                      <Loader2 className="text-muted size-4.5 animate-spin" />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="text-primary text-[17px] leading-6">{s.name}</p>
                    <p className="text-muted type-caption">
                      {subtitle}
                      {formatArea(s) && ` · ${formatArea(s)}`}
                    </p>
                  </div>
                  <p
                    className={`type-caption ${reading && !isDeferred ? "text-success" : "text-muted"}`}
                  >
                    {isDeferred ? "Skipped" : reading ? "Reading" : "Checking…"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton label="Finish setup" busy={busy} disabled onClick={onComplete} />
          {mayFinishAnyway && (
            <PillButton
              label="Finish anyway, we'll keep checking"
              variant="ghost"
              withArrow={false}
              busy={busy}
              onClick={onComplete}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Placement checklist: one row per sensor, mark placed or photo-check ──
  if (stage === "checklist") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="air_sensors"
          stage={stage}
          title={PRODUCT_NAMES.airQuality}
          onBack={() => setStage("tips")}
        />
        <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
        <div className="flex flex-1 flex-col gap-5 px-6 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Place each sensor</h1>
            <p className="text-muted text-[17px] leading-6">
              Put each one in its room, then mark it here. Snap a photo if you&apos;d like us to
              double-check the spot.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {sensors.map((s, i) => {
              const isPlaced = placed[i];
              const isDeferred = deferred[i];
              return (
                <div key={s.name} className="bg-background flex flex-col gap-3 rounded-2xl p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex size-9 shrink-0 items-center justify-center rounded-full ${
                        isPlaced ? "bg-success" : "bg-surface-subtle"
                      }`}
                    >
                      {isPlaced ? (
                        <Check className="size-4.5 text-white" strokeWidth={3} />
                      ) : (
                        <span className="text-primary text-[15px] leading-[22px]">{i + 1}</span>
                      )}
                    </div>
                    {/* No serial number. The sensors are labelled per room, so the
                        room name IS the identifier while walking the house; a
                        twelve-digit serial is unmatchable at arm's length. It
                        stays on the reading check, where support can use it. */}
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p className="text-primary text-[17px] leading-6">{s.name}</p>
                      {formatArea(s) && <p className="text-muted type-caption">{formatArea(s)}</p>}
                    </div>
                    {isPlaced && <p className="text-success type-caption">Placed</p>}
                    {isDeferred && <p className="text-muted type-caption">Not yet</p>}
                  </div>
                  {/* The merge was confirmed in the heating step; this is where
                      the resident holds the second sensor and would otherwise
                      look for its room. One sensor, placed for the whole space. */}
                  {absorbedBy(rooms, s).length > 0 && (
                    <p className="text-muted type-body-sm">
                      One thermostat heats this whole space, so it counts as one room. Place this
                      sensor roughly in the middle of it.
                    </p>
                  )}
                  {!isPlaced && (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => capturePhoto(i)}
                          aria-label="Confirm placement with a photo"
                          className="border-border text-muted flex shrink-0 items-center justify-center gap-1.5 rounded-md border bg-white px-4 py-2 text-[15px] leading-[22px]"
                        >
                          <Camera className="size-4" />
                          Photo
                        </button>
                        <button
                          type="button"
                          onClick={() => markPlaced(i)}
                          className="bg-accent text-accent-foreground active:bg-accent-pressed flex flex-1 items-center justify-center rounded-md py-2 text-[15px] leading-[22px]"
                        >
                          Placed
                        </button>
                      </div>
                      {!isDeferred && (
                        <button
                          type="button"
                          onClick={() => markDeferred(i)}
                          className="text-muted type-body-sm self-start underline [text-underline-position:from-font]"
                        >
                          Can&apos;t place this one yet
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {spareSensors.length > 0 && (
              <div className="border-border flex flex-col gap-1.5 rounded-2xl border border-dashed p-4">
                <p className="text-primary text-[17px] leading-6">
                  {spareSensors.length === 1 ? "A spare sensor" : "Spare sensors"}
                </p>
                {/* The room name is what is printed on the sensor's front, so
                    it is the one thing that tells the spare from the ones in use. */}
                <p className="text-muted type-body-sm">
                  {spareSensors.map((spare, k) => (
                    <span key={`${spare.label}-${k}`}>
                      {k > 0 && " "}
                      The sensor labelled <span className="text-primary">{spare.label}</span> is not
                      part of the setup
                      {spare.survivor
                        ? `: that room now counts as one with ${spare.survivor.name}.`
                        : "."}
                    </span>
                  ))}{" "}
                  Put {spareSensors.length === 1 ? "it" : "them"} anywhere you like, a kid&apos;s
                  room for example, to check the temperature and CO₂ there. SmartClimate will not
                  use {spareSensors.length === 1 ? "its" : "their"} readings.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton
            label={
              allResolved
                ? "Check they're reading"
                : `Place all sensors · ${placedCount}/${sensors.length}`
            }
            disabled={!allResolved}
            onClick={startReadingCheck}
          />
        </div>
      </div>
    );
  }

  // ── Photo confirmation for one sensor (opened from the checklist). Shows the
  //    captured shot while the AI check runs, then a friendly verdict. ────────
  const analysing = stage === "detecting";
  const goodPlacement = photoResult?.ok !== false;
  const goToChecklist = () => {
    markPlaced(photoIndex);
    setStage("checklist");
  };
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar
        stepKey="air_sensors"
        stage={stage}
        title={sensor.name}
        onBack={() => setStage("checklist")}
      />
      <PhaseDots total={dotsTotal} activeIndex={dotsIndex} />
      <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
        <div className="bg-background relative h-[418px] overflow-hidden rounded-2xl">
          <img
            src={photoUrl ?? "/brand/onboarding/sensor-room.png"}
            alt=""
            className="size-full object-cover"
          />
          <div
            className={`absolute bottom-4 left-4 flex items-center gap-2 rounded-sm px-3 py-2 ${
              analysing || !goodPlacement ? "bg-primary" : "bg-success"
            }`}
          >
            {analysing ? (
              <Loader2 className="size-4 animate-spin text-white" />
            ) : goodPlacement ? (
              <Check className="size-4 text-white" strokeWidth={3} />
            ) : (
              <AlertTriangle className="size-4 text-white" />
            )}
            <p className="text-[17px] leading-6 text-white">
              {analysing
                ? "Analyzing the photo…"
                : goodPlacement
                  ? "Placement looks good"
                  : "Take another look"}
            </p>
          </div>
        </div>
        {!analysing && photoResult && (
          <p className="text-muted text-[15px] leading-[22px]">{photoResult.message}</p>
        )}
      </div>
      {!analysing && (
        <div className="flex flex-col gap-1 px-6 pt-4 pb-7">
          {goodPlacement ? (
            <PillButton label="Looks good" onClick={goToChecklist} />
          ) : (
            <>
              <PillButton label="Retake photo" onClick={() => capturePhoto(photoIndex)} />
              <PillButton label="Place it anyway" variant="ghost" onClick={goToChecklist} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
